import { useState, useEffect, useRef } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useProfile, useUpdateProfile } from '../hooks/useProfile';
import { convertFeetInchesToCm } from '../utils/heightConversion';

import { FormField } from './FormField';
import { HeightInput } from './HeightInput';

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

/**
 * Auto-detect measurement system based on browser locale
 * US uses Imperial, rest of the world uses Metric
 */
function detectMeasurementSystem(): 'metric' | 'imperial' {
  const locale = navigator.language || 'en-US';
  return locale.startsWith('en-US') ? 'imperial' : 'metric';
}

/**
 * OnboardingModal Component
 *
 * Collects user demographics during first sign-in.
 * Shows a modal that blocks access until completed.
 */
export function OnboardingModal({ isOpen, onComplete }: OnboardingModalProps) {
  const updateProfile = useUpdateProfile();
  const { user } = useAuth();
  const { data: profile } = useProfile();

  const [email, setEmail] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [sexAssignedAtBirth, setSexAssignedAtBirth] = useState<
    'male' | 'female' | 'prefer-not-to-say'
  >('prefer-not-to-say');
  const [measurementSystem, setMeasurementSystem] = useState<
    'metric' | 'imperial'
  >(() => detectMeasurementSystem());
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Track if email has been manually edited to prevent race condition
  const emailEditedRef = useRef(false);

  // Initialize email from user's auth email or profile email
  // Only set if not manually edited to avoid overwriting user input
  useEffect(() => {
    if (!emailEditedRef.current) {
      if (user?.email) {
        setEmail(user.email);
      } else if (profile?.email) {
        setEmail(profile.email);
      }
    }
  }, [user, profile]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!email.trim()) {
      newErrors.email = 'Invalid email address';
    }

    if (!preferredName.trim()) {
      newErrors.preferredName = 'Preferred name is required';
    }

    if (!familyName.trim()) {
      newErrors.familyName = 'Family name is required';
    }

    if (!dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const dob = new Date(dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear();
      if (age < 13 || age > 120) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth';
      }
    }

    if (measurementSystem === 'metric') {
      if (!heightCm) {
        newErrors.heightCm = 'Height is required';
      } else {
        const height = parseFloat(heightCm);
        if (height < 50 || height > 300) {
          newErrors.heightCm = 'Height must be between 50 and 300 cm';
        }
      }
    } else {
      if (!heightFeet || !heightInches) {
        newErrors.height = 'Height is required';
      } else {
        const feet = parseInt(heightFeet);
        const inches = parseInt(heightInches);
        if (feet < 1 || feet > 9 || inches < 0 || inches >= 12) {
          newErrors.height = 'Please enter a valid height';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      // Convert height to cm if imperial
      let heightInCm: number;
      if (measurementSystem === 'metric') {
        heightInCm = parseFloat(heightCm);
      } else {
        const feet = parseInt(heightFeet);
        const inches = parseInt(heightInches);
        heightInCm = convertFeetInchesToCm(feet, inches);
      }

      await updateProfile.mutateAsync({
        email,
        preferredName,
        familyName,
        dateOfBirth,
        heightCm: heightInCm,
        sexAssignedAtBirth,
        measurementSystem,
        metadata: {
          onboardingCompleted: true,
        },
      });

      onComplete();
    } catch {
      setErrors({
        submit: 'Failed to save profile. Please try again.',
      });
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-2 text-text-dark">
            Help us personalize your experience
          </h2>
          <p className="text-text-secondary mb-6">
            We need a few details to provide you with personalized health
            recommendations.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              id="email"
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                emailEditedRef.current = true;
              }}
              error={errors.email}
              placeholder="your.email@example.com"
              required
            />

            <FormField
              id="preferredName"
              label="Preferred Name"
              value={preferredName}
              onChange={(e) => setPreferredName(e.target.value)}
              error={errors.preferredName}
              placeholder="What should we call you?"
              required
            />

            <FormField
              id="familyName"
              label="Family Name"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              error={errors.familyName}
              placeholder="Your last name"
              required
            />

            <FormField
              id="dateOfBirth"
              label="Date of Birth"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              error={errors.dateOfBirth}
              required
            />

            {/* Measurement System */}
            <div>
              <label className="block text-sm font-medium mb-2 text-text-dark">
                Measurement System *
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="measurementSystem"
                    value="metric"
                    checked={measurementSystem === 'metric'}
                    onChange={(e) =>
                      setMeasurementSystem(e.target.value as 'metric')
                    }
                    className="mr-2"
                  />
                  Metric (cm, kg)
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="measurementSystem"
                    value="imperial"
                    checked={measurementSystem === 'imperial'}
                    onChange={(e) =>
                      setMeasurementSystem(e.target.value as 'imperial')
                    }
                    className="mr-2"
                  />
                  Imperial (ft/in, lb)
                </label>
              </div>
            </div>

            <HeightInput
              value={{
                heightCm,
                heightFeet,
                heightInches,
              }}
              onChange={(value) => {
                if (value.heightCm !== undefined) setHeightCm(value.heightCm);
                if (value.heightFeet !== undefined)
                  setHeightFeet(value.heightFeet);
                if (value.heightInches !== undefined)
                  setHeightInches(value.heightInches);
              }}
              measurementSystem={measurementSystem}
              error={errors.heightCm || errors.height}
              required
            />

            <FormField
              id="sexAssignedAtBirth"
              label="Sex Assigned at Birth"
              type="select"
              value={sexAssignedAtBirth}
              onChange={(e) =>
                setSexAssignedAtBirth(
                  e.target.value as 'male' | 'female' | 'prefer-not-to-say'
                )
              }
              description="Used for metabolic rate and health calculations based on biological factors"
              required
              options={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'prefer-not-to-say', label: 'Prefer not to say' },
              ]}
            />

            {errors.submit && (
              <p className="text-red-600 text-sm">{errors.submit}</p>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="submit"
                disabled={updateProfile.isPending}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateProfile.isPending ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
